/* eslint-disable max-lines */
import { toast } from 'react-toastify';
import { useContext, useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import UserService from '../../../../services/User';
import { Row, Col, FormGroup, Input, Label } from 'reactstrap';
import HireDocuments from '../../../../services/HireDocuments';
import EmploymentDocumentsService from '../../../../services/EmploymentDocuments';
import VerifyEmploymentDocumentsModal, {
  DocumentVerificationAnswer,
  VerifiableDocument,
} from './VerifyEmploymentDocumentsModal';
import DocumentModal from './DocumentModal';
import confirmAlert from '../../../../helpers/confirmAlert';
import confirmAlertWithForm from '../../../../helpers/confirmAlertWithForm';
import NewHireDocumentsView from './NewHireDocumentsView';
import I9DocumentModal from '../../NewHire/I9DocumentModal';
import I9SpanishDocumentModal from '../../NewHire/I9SpanishDocumentModal';
import OpacityLoaderSpinner from '../../../../components/Core/OpacityLoaderSpinner';

interface FilesTabProps {
  reload: boolean;
}

const FilesTab = ({ reload }: FilesTabProps) => {
  const { id: userId } = useParams<{ id: string }>();
  const { token, user }: any = useContext(AuthContext);
  const [currentUser, setCurrentUser] = useState<any>({});
  const [showDocument, setShowDocument] = useState<boolean>(false);
  const [documentsData, setDocumentsData] = useState<any[]>();
  const [currentDocument, setCurrentDocument] = useState<any[]>();
  const [currentI9, setCurrentI9] = useState<any>();
  const [showModalI9, setShowModalI9] = useState<boolean>(false);
  const [showModalI9Spanish, setShowModalI9Spanish] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [verification, setVerification] = useState<{ approveId: string; documents: VerifiableDocument[] } | null>(null);
  const refreshNewHireDocumentsRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      if (!userId) return;
      await getUserById();
      if (isMounted) {
        setInitialLoading(false);
      }
    };
    loadData();
    return () => {
      isMounted = false;
    };
  }, [userId]);

  useEffect(() => {
    getDocuments();
  }, [reload]);

  const getUserById = async () => {
    try {
      const result = await UserService(token).show(userId);
      setCurrentUser(result?.data || {});
    } catch (e: any) {
      console.error(e?.response?.data?.message || 'Invalid user ID');
    }
  };

  const handleDocument = (d: any) => {
    setCurrentDocument(d);
    setShowDocument(true);
  };

  const getDocuments = async () => {
    try {
      const result = await HireDocuments('').show(userId);
      const documents: any[] = result?.data || [];
      setDocumentsData(documents);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Invalid user ID');
    }
  };

  const handleSubmitDenyForm = async (e: React.FormEvent<HTMLFormElement>, id: string) => {
    e.preventDefault();
    try {
      const data = new FormData(e.target as HTMLFormElement);
      const body = {} as any;
      for (const [key, value] of data.entries()) {
        (body as any)[key] = value;
      }
      await deleteDocument(id, body);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Invalid user ID');
    }
  };

  const loadPendingEmploymentDocuments = async (): Promise<VerifiableDocument[]> => {
    const employeeId = currentUser?.employee?.id;
    if (!employeeId) return [];
    const result = await EmploymentDocumentsService(token).list(employeeId);
    return (result?.data || [])
      .filter((d: any) => !d?.verifiedAt)
      .map((d: any) => ({ id: d?.id, title: d?.documentType, url: d?.document }));
  };

  const approveDocument = async (id: string, documents?: DocumentVerificationAnswer[]) => {
    await HireDocuments(token).approve(id, documents ? { documents } : {});
    toast.success('Document approved successfully!');
    await getDocuments();
    if (refreshNewHireDocumentsRef.current) {
      await refreshNewHireDocumentsRef.current();
    }
  };

  const handleSubmitApproveForm = async (e: React.FormEvent<HTMLFormElement>, id: string) => {
    e?.preventDefault();
    try {
      const pendingDocuments = await loadPendingEmploymentDocuments();
      if (pendingDocuments.length) {
        setShowModalI9(false);
        setShowModalI9Spanish(false);
        setVerification({ approveId: id, documents: pendingDocuments });
        return;
      }
      await approveDocument(id);
    } catch (e: any) {
      console.log(e);
      toast.error(e?.response?.data?.message || 'Invalid user ID');
    }
  };

  const handleConfirmVerification = async (answers: DocumentVerificationAnswer[]) => {
    if (!verification) return;
    try {
      await approveDocument(verification.approveId, answers);
      setVerification(null);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not approve the I-9');
    }
  };

  const removeAlert = async (e: any, id: string) => {
    e.preventDefault();
    return confirmAlert({
      title: 'Delete',
      messageType: 'DELETE',
      message: 'Are you sure to delete this document?',
      onClickYes: () => deleteDocument(id),
    });
  };

  const denyAlert = async (e: any, id: string) => {
    e.preventDefault();
    return confirmAlertWithForm({
      title: 'Delete',
      messageType: 'DELETE',
      message: 'Are you sure to deny this document?',
      children: (
        <Row>
          <Col>
            <FormGroup className="form-control-label">
              <Label>Reason</Label>
              <Input type="textarea" name="reason" />
            </FormGroup>
          </Col>
        </Row>
      ),
      onSubmit: (e) => handleSubmitDenyForm(e, id),
    });
  };

  const handleApprove = async (e: any, id: string) => {
    e.preventDefault();
    const d = documentsData?.find((d) => d?.id === id);
    if (d?.type === 'I9' && !d?.uploadedPdf) {
      setCurrentI9(d);
      return setShowModalI9(true);
    }
    if (d?.type === 'I9-Spanish' && !d?.uploadedPdf) {
      setCurrentI9(d);
      return setShowModalI9Spanish(true);
    }
    approveAlert(e, id);
  };

  const approveAlert = async (e: any, id: string) => {
    e.preventDefault();
    return confirmAlertWithForm({
      title: 'Approve',
      messageType: 'SAVE',
      message: 'Are you sure to approve this document?',
      onSubmit: (e) => handleSubmitApproveForm(e, id),
    });
  };

  const deleteDocument = async (id: string, body?: any) => {
    try {
      await HireDocuments(token).delete(id, body);
      toast.success('Document deleted successfully!');
      await getDocuments();
      if (refreshNewHireDocumentsRef.current) {
        await refreshNewHireDocumentsRef.current();
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Invalid ID');
    }
  };

  if (initialLoading) {
    return <OpacityLoaderSpinner />;
  }

  return (
    <>
      <NewHireDocumentsView
        loggedUser={user}
        currentUser={currentUser}
        token={token}
        removeAlert={removeAlert}
        denyAlert={denyAlert}
        handleDocument={handleDocument}
        approveAlert={handleApprove}
        onDocumentsUpdated={(refreshFn) => {
          refreshNewHireDocumentsRef.current = refreshFn;
        }}
      />
      <DocumentModal data={currentDocument} isOpen={showDocument} toggleModal={() => setShowDocument(false)} />
      <VerifyEmploymentDocumentsModal
        isOpen={!!verification}
        title="Approve I-9"
        description="Enter the expiration date printed on each uploaded document, or mark it as not expiring."
        confirmLabel="Approve I-9"
        documents={verification?.documents || []}
        toggleModal={() => setVerification(null)}
        onConfirm={handleConfirmVerification}
      />
      <I9DocumentModal
        isOpen={showModalI9}
        token={token}
        loggedUser={user}
        currentUser={currentUser}
        currentI9={currentI9}
        isApproval={true}
        toggleModal={(e) => {
          e.preventDefault();
          setShowModalI9(!showModalI9);
        }}
        saveModal={async (e) => {
          e.preventDefault();
          await handleSubmitApproveForm(e, currentI9?.id);
          await getDocuments();
          if (refreshNewHireDocumentsRef.current) {
            await refreshNewHireDocumentsRef.current();
          }
        }}
      />
      <I9SpanishDocumentModal
        isOpen={showModalI9Spanish}
        token={token}
        loggedUser={user}
        currentUser={currentUser}
        currentI9={currentI9}
        isApproval={true}
        toggleModal={(e) => {
          e.preventDefault();
          setShowModalI9Spanish(!showModalI9Spanish);
        }}
        saveModal={async (e) => {
          e.preventDefault();
          await handleSubmitApproveForm(e, currentI9?.id);
          await getDocuments();
          if (refreshNewHireDocumentsRef.current) {
            await refreshNewHireDocumentsRef.current();
          }
        }}
      />
    </>
  );
};

export default FilesTab;
